import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ClearIcon from '@mui/icons-material/Clear';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { searchCPTExams, selectExam, setManuallySet, clearSearch } from '../store/cptExamSlice';
import cptExamService, { buildCPTLabel, type CPTExam } from '../services/cptExamService';

interface CPTChipComboProps {
  disabled?: boolean;
  onSelect?: (exam: CPTExam | null) => void;
  onClear?: () => void;
}

/**
 * Inline editable CPT chip for the toolbar.
 *
 * Idle state  — renders as a styled Chip showing "XXXXX — Exam Name".
 * Edit state  — the chip expands into an Autocomplete combo:
 *   • ≥ 3 chars → fires both:
 *       1. DB search  (/cpt/exams?q=...) for broad catalog matches
 *       2. Rule-engine detection (/cpt/detect?transcript=...) for natural-
 *          language input like "mri knee" — result is merged at the top
 *   • Options are de-duplicated and the rule-engine suggestion is pinned first.
 */
const CPTChipCombo: React.FC<CPTChipComboProps> = ({ disabled = false, onSelect, onClear }) => {
  const dispatch = useAppDispatch();
  const { selectedExam, searchResults, searchLoading } = useAppSelector((s) => s.cptExams);

  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [ruleOptions, setRuleOptions] = useState<CPTExam[]>([]);
  const [ruleLoading, setRuleLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when edit mode opens
  useEffect(() => {
    if (editing) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [editing]);

  // DB search — debounced, fires at ≥ 3 chars
  useEffect(() => {
    if (!editing) return;
    if (inputValue.length < 3) {
      dispatch(clearSearch());
      return;
    }
    const t = setTimeout(() => {
      dispatch(searchCPTExams({ q: inputValue, limit: 25 }));
    }, 250);
    return () => clearTimeout(t);
  }, [inputValue, editing, dispatch]);

  // Rule-engine detection — fires at ≥ 3 chars for natural-language input
  useEffect(() => {
    if (!editing || inputValue.length < 3) {
      setRuleOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      setRuleLoading(true);
      try {
        const result = await cptExamService.detectFromTranscript(inputValue, false);
        if (cancelled) return;
        if (result?.cpt_code) {
          const ruleExam: CPTExam = {
            id: 0,
            cpt_code: result.cpt_code,
            exam_name: result.exam_name,
            modality: result.modality,
            body_region: result.body_region,
            subspecialty: null,
            laterality: result.laterality ?? null,
            contrast: result.contrast ?? null,
            description: '(Rule-engine suggestion)',
            report_template: null,
            is_active: true,
          };
          setRuleOptions([ruleExam]);
        } else {
          setRuleOptions([]);
        }
      } catch {
        if (!cancelled) setRuleOptions([]);
      } finally {
        if (!cancelled) setRuleLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [inputValue, editing]);

  // Merge rule suggestion (pinned first) + DB results (de-duplicated)
  const mergedOptions: CPTExam[] = React.useMemo(() => {
    const dbCodes = new Set(searchResults.map((e) => e.cpt_code));
    const rulePinned = ruleOptions.filter((r) => !dbCodes.has(r.cpt_code) || ruleOptions.length > 0);
    // Rule suggestion first, then DB results; remove exact duplicate CPT codes
    const seen = new Set<string>();
    const out: CPTExam[] = [];
    for (const e of [...rulePinned, ...searchResults]) {
      if (!seen.has(e.cpt_code)) {
        seen.add(e.cpt_code);
        out.push(e);
      }
    }
    return out;
  }, [ruleOptions, searchResults]);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setInputValue('');
    setEditing(true);
  }, [disabled]);

  const handleClose = useCallback(() => {
    setEditing(false);
    setInputValue('');
    dispatch(clearSearch());
    setRuleOptions([]);
  }, [dispatch]);

  const handleChange = useCallback(
    (_e: React.SyntheticEvent, value: CPTExam | null) => {
      if (value) {
        dispatch(selectExam(value));
        dispatch(setManuallySet(true));
        onSelect?.(value);
      }
      handleClose();
    },
    [dispatch, onSelect, handleClose],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      dispatch(selectExam(null));
      dispatch(setManuallySet(true));
      onClear?.();
    },
    [dispatch, onClear],
  );

  const isLoading = searchLoading || ruleLoading;

  // ── Edit mode ────────────────────────────────────────────────────────
  if (editing) {
    return (
      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ ml: 1, width: 340 }}>
          <Autocomplete
            open
            size="small"
            options={mergedOptions}
            loading={isLoading}
            inputValue={inputValue}
            onInputChange={(_e, val, reason) => {
              if (reason !== 'reset') setInputValue(val);
            }}
            onChange={handleChange}
            onClose={(_e, reason) => {
              if (reason === 'escape') handleClose();
            }}
            getOptionLabel={(o) => {
              const parts = [o.exam_name];
              if (o.laterality) parts.push(o.laterality);
              if (o.contrast && o.contrast !== 'none') {
                parts.push(o.contrast.replace(/_/g, ' '));
              }
              return `${o.cpt_code} — ${parts.join(', ')}`;
            }}
            isOptionEqualToValue={(a, b) => a.cpt_code === b.cpt_code}
            filterOptions={(x) => x}
            groupBy={(o) =>
              o.description === '(Rule-engine suggestion)' ? '🔍 Rule-engine match' : '📋 Catalog results'
            }
            renderOption={(props, option) => (
              <li {...props} key={`${option.cpt_code}-${option.description}`}>
                <Box sx={{ py: 0.25 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {option.cpt_code} — {option.exam_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.modality} · {option.body_region}
                    {option.laterality ? ` · ${option.laterality}` : ''}
                    {option.contrast && option.contrast !== 'none'
                      ? ` · ${option.contrast.replace(/_/g, ' ')}`
                      : ''}
                  </Typography>
                  {option.description && option.description !== '(Rule-engine suggestion)' && (
                    <Typography variant="caption" color="text.disabled" display="block">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            noOptionsText={
              inputValue.length < 3
                ? 'Type at least 3 characters…'
                : isLoading
                ? 'Searching…'
                : 'No CPT exams found'
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                placeholder="CPT code or exam name…"
                autoFocus
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading && <CircularProgress color="inherit" size={14} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.78rem',
                    bgcolor: 'background.paper',
                  },
                }}
              />
            )}
            sx={{ width: '100%' }}
          />
        </Box>
      </ClickAwayListener>
    );
  }

  // ── Idle chip — always rendered ────────────────────────────────────
  if (selectedExam) {
    return (
      <Tooltip title={disabled ? '' : 'Click to change exam · ✕ to clear'} arrow>
        <Chip
          id="cpt-exam-chip"
          icon={<EditIcon sx={{ fontSize: '0.85rem !important' }} />}
          label={buildCPTLabel(selectedExam)}
          size="small"
          color="primary"
          variant="outlined"
          onClick={disabled ? undefined : handleOpen}
          onDelete={disabled ? undefined : handleClear}
          deleteIcon={<ClearIcon fontSize="small" />}
          sx={{ cursor: disabled ? 'default' : 'pointer', maxWidth: 360 }}
          aria-label={`CPT exam: ${buildCPTLabel(selectedExam)}. Click to change, or click X to clear.`}
        />
      </Tooltip>
    );
  }

  // ── Empty placeholder — click to search ──────────────────────────────
  return (
    <Tooltip title={disabled ? '' : 'Click to assign a CPT exam'} arrow>
      <Chip
        id="cpt-exam-chip"
        icon={<EditIcon sx={{ fontSize: '0.85rem !important', opacity: 0.5 }} />}
        label="Assign CPT exam…"
        size="small"
        variant="outlined"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          borderStyle: 'dashed',
          color: 'text.secondary',
          borderColor: 'divider',
          maxWidth: 220,
        }}
        aria-label="No CPT exam assigned. Click to search and assign."
      />
    </Tooltip>
  );
};

export default CPTChipCombo;
